import chatWindowHistory from "./chatWindowHistory";
import "../assets/css/desktop-chat.css";

const messageKey = message => message && String(message.client_msg_id || message.id);

export default {
  mixins: [chatWindowHistory],
  data() {
    return { scrollAffix: false, historyAnchor: null, desktopVisibleUid: null,
      desktopScrollEpoch: 0, desktopScrollFrame: null, desktopPendingLayout: null };
  },
  computed: {
    desktopViewKey() { return this.data ? "window" : this.service ? "service" : "main"; },
    desktopWindowOpen() {
      if (this.data) return this.data.show !== false;
      return this.service ? this.showchatwindow : true;
    },
    desktopMessagesVisible() {
      return !!this.historyConversation.uid && this.desktopVisibleUid == this.historyConversation.uid;
    },
    desktopInitialLoading() {
      return !!this.historyConversation.uid && !this.desktopMessagesVisible && !this.historyConversation.historyError;
    },
  },
  methods: {
    scrollContainer() {
      const scrollbar = this.$refs.scrollbarRef;
      return scrollbar && scrollbar.$el.querySelector(".el-scrollbar__wrap");
    },
    savedDesktopPosition() {
      const positions = this.historyConversation.desktopScrollPositions || {};
      return positions[this.desktopViewKey];
    },
    readDesktopPosition(following = !this.scrollAffix) {
      const container = this.scrollContainer();
      if (!container) return null;
      const top = container.getBoundingClientRect().top;
      const node = Array.from(container.querySelectorAll("[data-chat-message-id]"))
        .find(item => item.getBoundingClientRect().bottom > top);
      return { top: container.scrollTop, following,
        anchor: node ? { id: node.getAttribute("data-chat-message-id"), offset: node.getBoundingClientRect().top - top } : null };
    },
    rememberDesktopPosition(address = this.historyConversation.uid) {
      if (!address || this.desktopVisibleUid != address) return;
      const position = this.readDesktopPosition();
      if (position) this.$store.commit("rememberDesktopChatPosition", { address, view: this.desktopViewKey, position });
    },
    cancelDesktopLayout() {
      this.desktopScrollEpoch++;
      if (this.desktopScrollFrame !== null) cancelAnimationFrame(this.desktopScrollFrame);
      this.desktopScrollFrame = null;
      this.desktopPendingLayout = null;
    },
    // Keep content hidden until this conversation has been laid out. An old
    // nextTick/frame must never use the scrollbar belonging to a new selection.
    queueDesktopLayout(position = this.savedDesktopPosition()) {
      this.cancelDesktopLayout();
      const epoch = this.desktopScrollEpoch;
      const uid = this.historyConversation.uid;
      this.desktopPendingLayout = { uid, position };
      this.$nextTick(() => {
        if (epoch !== this.desktopScrollEpoch || this.historyConversation.uid != uid || !this.desktopWindowOpen) return;
        this.desktopScrollFrame = requestAnimationFrame(() => {
          if (epoch !== this.desktopScrollEpoch || this.historyConversation.uid != uid || !this.desktopWindowOpen) return;
          this.desktopScrollFrame = null;
          this.desktopPendingLayout = null;
          const container = this.scrollContainer();
          if (!container) return;
          const scrollbar = this.$refs.scrollbarRef;
          if (scrollbar.update) scrollbar.update();
          this.scrollAffix = !!position && position.following === false;
          let top = position && position.top;
          if (!this.scrollAffix) top = container.scrollHeight - container.clientHeight;
          else if (position.anchor) {
            const node = Array.from(container.querySelectorAll("[data-chat-message-id]"))
              .find(item => item.getAttribute("data-chat-message-id") === position.anchor.id);
            if (node) top = container.scrollTop + node.getBoundingClientRect().top
              - container.getBoundingClientRect().top - position.anchor.offset;
          }
          container.scrollTop = Math.max(0, Math.min(top || 0, container.scrollHeight - container.clientHeight));
          this.desktopVisibleUid = uid;
          this.rememberDesktopPosition(uid);
        });
      });
    },
    refreshDesktopView() {
      const user = this.historyConversation;
      if (user.uid && (user.historyLoaded === true || user.historyError || this.desktopMessagesVisible)) {
        this.queueDesktopLayout();
      }
    },
    chatwindowscrollevent() {
      const container = this.scrollContainer();
      if (!container || !this.desktopMessagesVisible || !this.desktopWindowOpen) return;
      this.scrollAffix = container.scrollHeight - container.scrollTop - container.clientHeight > 20;
      this.rememberDesktopPosition();
    },
    captureHistoryPosition() {
      this.historyAnchor = { uid: this.historyConversation.uid, position: this.readDesktopPosition(false) };
    },
    handleImageLoad() {
      if (!this.desktopMessagesVisible) return;
      // A fast preview may load before the own-send layout reaches its frame.
      const pending = this.desktopPendingLayout;
      this.queueDesktopLayout(pending && pending.uid == this.historyConversation.uid
        ? pending.position : this.savedDesktopPosition());
    },
  },
  watch: {
    "historyConversation.uid": {
      immediate: true,
      handler(uid, previousUid) {
        this.rememberDesktopPosition(previousUid);
        this.cancelDesktopLayout();
        this.desktopVisibleUid = null;
        this.historyAnchor = null;
        const position = this.savedDesktopPosition();
        this.scrollAffix = !!position && position.following === false;
        this.refreshDesktopView();
      },
    },
    "historyConversation.historyLoaded"() { this.refreshDesktopView(); },
    "historyConversation.historyError"() { this.refreshDesktopView(); },
    desktopWindowOpen(open) {
      if (open) this.refreshDesktopView();
      else { this.rememberDesktopPosition(); this.cancelDesktopLayout(); }
    },
    messages(messages, previous = []) {
      if (!this.desktopMessagesVisible) { this.refreshDesktopView(); return; }
      const first = messageKey(previous[0]);
      if (this.historyAnchor && this.historyAnchor.uid == this.historyConversation.uid &&
          first && messages.findIndex(message => messageKey(message) === first) > 0) {
        // The watcher runs before DOM updates: keep the latest reading position,
        // including any scrolling done while the older-history request was pending.
        const position = this.readDesktopPosition(false);
        this.historyAnchor = null;
        this.queueDesktopLayout(position);
        return;
      }
      const keys = new Set(previous.map(messageKey));
      const latestId = previous.reduce((id, message) => Math.max(id, Number(message.id) || 0), 0);
      // Pending photos have no persisted id. Older history can also be added by
      // another window, and must not be treated as a newly sent message.
      if (messages.some(message => message.sender_id == this.$store.state.User.id && !keys.has(messageKey(message)) &&
          (!Number(message.id) || Number(message.id) > latestId))) {
        this.scrollAffix = false;
      }
      this.queueDesktopLayout(this.readDesktopPosition());
    },
  },
  mounted() { this.refreshDesktopView(); },
  beforeUnmount() { this.rememberDesktopPosition(); this.cancelDesktopLayout(); },
};
